/* eslint-disable no-console */
import axiosStatic, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { handlePolicyConflictError } from 'api/project-policy/error-handler';
import {
  getCurrentApiHost,
  API_NAMESPACE,
  API_VERSION,
} from 'config/environment';
// import { toastService } from "components/molecules/toast";
import { ApiKey, logger, CustomerPreview } from 'services';
import { logout } from 'utils/logout';
import { addHopIdToHeader } from 'utils/window-utils';

const EXCLUDE_LOGOUT_PATHS = ['/setup-profile', '/login', '/2fa'];

/** Wire name for the initiator header (hyphenated; not a valid TS object literal key for naming rules). */
const INITIATOR_SOURCE_HEADER = 'initiator-source';

// Create config function that uses current API host dynamically
const getConfig = (): InternalAxiosRequestConfig<any> => ({
  baseURL: `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`,
  adapter: 'fetch',
  headers: new AxiosHeaders(),
});

const withCustomHeader = (
  header: Record<string, string>,
  configuration?: InternalAxiosRequestConfig<any>,
): InternalAxiosRequestConfig<any> => {
  const configToUse = configuration || getConfig();

  return {
    ...configToUse,
    headers: configToUse.headers.set(header),
  };
};

const isMethodAllowedInPreview = (method = '', url = ''): boolean =>
  /(get)/i.test(method) ||
  (/(post)/i.test(method) &&
    (url.includes('portal') ||
      url.includes('resolve-widgets') ||
      url.includes('dashboard') ||
      url.includes('fields/searchLight') ||
      url.includes('tasks/searchlightV1') ||
      url.includes('pivot') ||
      url.includes('localhost:12345') ||
      url.includes('kanbanLayout') ||
      url.includes('kanbanV1ForProjectBoard') ||
      url.includes('kanbanV1ColForProjectBoard') ||
      (url.includes('run') && url.includes('server-action'))));

const injectApiKey = (configuration: InternalAxiosRequestConfig<any>) => {
  return Promise.resolve(withCustomHeader(ApiKey.header(), configuration));
};

const injectPreviewApiKey = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  return Promise.resolve(
    withCustomHeader(CustomerPreview.header(), configuration),
  );
};

const removeCookieIfPreview = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  const [isPreviewMode] = await CustomerPreview.isPreviewMode();

  if (!isPreviewMode) {
    return Promise.resolve(configuration);
  }

  return Promise.resolve({
    ...configuration,
    fetchOptions: { credentials: 'omit' },
  });
};

const injectHopId = (configuration: InternalAxiosRequestConfig<any>) =>
  Promise.resolve(withCustomHeader(addHopIdToHeader(), configuration));

const injectCommonRequestHeaders = (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  let config: InternalAxiosRequestConfig<any> = configuration;

  config = withCustomHeader({ [INITIATOR_SOURCE_HEADER]: 'UI' }, config);

  return Promise.resolve(config);
};

// Interceptor to ensure baseURL always uses current API host (updated after cookiesEnabled check)
const updateBaseUrlToCurrentHost = (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  const currentHost = getCurrentApiHost();

  // Update the host while preserving the path (e.g. /api/v2 stays /api/v2)
  if (configuration.baseURL) {
    const updatedBaseURL = configuration.baseURL.replace(
      /^https?:\/\/[^/]+/,
      currentHost!,
    );

    return Promise.resolve({
      ...configuration,
      baseURL: updatedBaseURL,
    });
  }

  return Promise.resolve({
    ...configuration,
    baseURL: `${currentHost}/${API_NAMESPACE}/${API_VERSION}`,
  });
};

const rejectWriteRequestsInPreview = async (
  configuration: InternalAxiosRequestConfig<any>,
) => {
  if (isMethodAllowedInPreview(configuration?.method, configuration?.url)) {
    return Promise.resolve(configuration);
  }
  const [preview] = await CustomerPreview.isPreviewMode();

  if (preview) {
    throw new Error('editing disallowed in preview mode');
  }

  return Promise.resolve(configuration);
};

enum ApiErrorCode {
  InvalidKey = 'INVALID_KEY',
  NotAuthorized = 'NOT_AUTHORIZED',
}

export enum ApiStatusCode {
  NotAuthorized = 401,
  PolicyConflict = 422,
  TooManyRequests = 429,
}

/**
 * Dispatches a failed-request log to the right severity bucket.
 *
 * Datadog's `service:rocket-frontend status:error` filter is meant for
 * actionable failures (real backend bugs, network outages). Routine 4xx
 * responses (401 token-expired → logout, 403 permission-denied → no-access
 * page, 429 rate-limit → FE retries, 400 validation → surfaced in the form)
 * are expected app states, not crashes. Logging them at `error` severity
 * floods the error stream and makes real crashes invisible.
 *
 * Mapping:
 *   - 5xx                      → `logger.error` (real backend failure)
 *   - 4xx                      → `logger.warn`  (expected app state)
 *   - 0 / no response (network) → `logger.error` (real network failure)
 *
 * The wording of the log message is unchanged so existing Datadog queries
 * that match on `Request failed with status code …` keep working — only
 * the `status` facet moves.
 */
export const logAxiosFailure = (error: AxiosError<any>): void => {
  const status = error.response?.status;
  const message = error.message || 'Request failed';
  const context = { error, status, url: error.config?.url };

  if (status === undefined || status === 0) {
    // Network error / request never made it to the server — still actionable.
    logger.error(message, context);

    return;
  }

  if (status >= 500) {
    logger.error(message, context);

    return;
  }

  // 4xx — demoted to warn. Still queryable via `status:warn`.
  logger.warn(message, context);
};

export const handleAxiosError = (error: AxiosError<any>): Promise<never> => {
  logAxiosFailure(error);

  if (
    error.response?.status === ApiStatusCode.NotAuthorized &&
    (error.response?.data?.errors?.[0]?.errorCode ===
      ApiErrorCode.NotAuthorized ||
      error.response?.data?.[0]?.errorCode === ApiErrorCode.NotAuthorized)
  ) {
    if (!EXCLUDE_LOGOUT_PATHS.includes(window.location.pathname)) {
      logout();
    }
  } else if (error.response?.status === ApiStatusCode.PolicyConflict) {
    handlePolicyConflictError(error);
  } else if (error.response?.status === ApiStatusCode.TooManyRequests) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const retryAt = error.response?.headers['x-retry-after'];

    // * Commenting this because of circular dependency

    // toastService({
    //   title: "Too many requests!",
    //   message: `You have exceeded the request limit.${
    //     retryAt ? ` Please try again in ${format("m", retryAt)}.` : ""
    //   }`,
    //   duration: 10,
    // }).error();
  }

  return Promise.reject(error);
};

const attachAxiosInterceptors = (client: AxiosInstance): void => {
  client.interceptors.request.use(updateBaseUrlToCurrentHost);
  client.interceptors.request.use(injectPreviewApiKey);
  client.interceptors.request.use(injectApiKey);
  client.interceptors.request.use(removeCookieIfPreview);
  client.interceptors.request.use(injectHopId);
  client.interceptors.request.use(injectCommonRequestHeaders);
  client.interceptors.request.use(rejectWriteRequestsInPreview);
  client.interceptors.response.use((response) => response, handleAxiosError);
};

export const axios = (): Observable<AxiosInstance> =>
  ApiKey.tokens().pipe(
    map(() =>
      axiosStatic.create(withCustomHeader(ApiKey.header(), getConfig())),
    ),
    tap(attachAxiosInterceptors),
    shareReplay(1),
  );
